import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { DownloadCloud, Plus, FileText, Loader2, Cloud, PenLine } from "lucide-react";

function fmt(iso) { try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); } catch { return iso; } }

export default function NotesPanel({ clinikoId, onNotesChange }) {
  const [notes, setNotes] = useState([]);
  const [pulling, setPulling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ subjective: "", objective: "", assessment: "", plan: "" });
  const [open, setOpen] = useState(false);

  const load = () => clinikoId && api.get(`/patients/${clinikoId}/notes`).then((r) => { setNotes(r.data); onNotesChange?.(r.data); }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clinikoId]);

  const pull = async () => {
    setPulling(true);
    try {
      const { data } = await api.post(`/patients/${clinikoId}/notes/pull-cliniko`);
      setNotes(data.notes); onNotesChange?.(data.notes);
      toast.success(`Pulled ${data.count} notes from Cliniko`);
    } catch { toast.error("Could not pull Cliniko notes — retry"); }
    finally { setPulling(false); }
  };

  const save = async () => {
    if (!form.assessment && !form.subjective) { toast.error("Add at least a subjective or assessment note"); return; }
    setSaving(true);
    try {
      await api.post(`/patients/${clinikoId}/notes`, form);
      setForm({ subjective: "", objective: "", assessment: "", plan: "" });
      setOpen(false);
      toast.success("Clinical note saved");
      load();
    } catch { toast.error("Failed to save note"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={pull} disabled={pulling} variant="outline" data-testid="pull-cliniko-notes-btn" className="border-teal-200 text-teal-700 hover:bg-teal-50">
          {pulling ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <DownloadCloud className="mr-1.5 h-4 w-4" />}
          Pull from Cliniko
        </Button>
        <Button onClick={() => setOpen((o) => !o)} data-testid="new-note-toggle-btn" className="bg-teal-600 hover:bg-teal-700">
          <Plus className="mr-1.5 h-4 w-4" /> Write note
        </Button>
      </div>

      {open && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><PenLine className="h-4 w-4 text-teal-600" /> New SOAP note</div>
          <div className="grid gap-3 sm:grid-cols-2">
            {["subjective", "objective", "assessment", "plan"].map((f) => (
              <div key={f}>
                <Label className="text-xs capitalize text-slate-500">{f}</Label>
                <Textarea value={form[f]} onChange={(e) => setForm({ ...form, [f]: e.target.value })} data-testid={`note-${f}-input`} className="mt-1 min-h-[70px] text-sm" placeholder={`${f}...`} />
              </div>
            ))}
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="clinical-notes-save-btn" className="bg-teal-600 hover:bg-teal-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save note"}
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2.5" data-testid="notes-timeline">
        {notes.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-400">
            <FileText className="mx-auto mb-2 h-6 w-6" /> No notes yet. Pull from Cliniko or write one.
          </div>
        )}
        {notes.map((n) => (
          <div key={n.id || n.created_at} className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm" data-testid="note-card">
            <div className="mb-2 flex items-center justify-between">
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${n.source === "cliniko" ? "bg-teal-50 text-teal-700" : "bg-indigo-50 text-indigo-700"}`}>
                {n.source === "cliniko" ? <Cloud className="h-3 w-3" /> : <PenLine className="h-3 w-3" />}
                {n.source === "cliniko" ? "Cliniko" : "In-app"}
              </span>
              <span className="font-mono text-[11px] text-slate-400">{fmt(n.created_at)} · {n.author}</span>
            </div>
            <div className="grid gap-1.5 text-sm sm:grid-cols-2">
              {n.subjective && <p><b className="text-slate-400">S:</b> {n.subjective}</p>}
              {n.objective && <p><b className="text-slate-400">O:</b> {n.objective}</p>}
              {n.assessment && <p><b className="text-slate-400">A:</b> {n.assessment}</p>}
              {n.plan && <p><b className="text-slate-400">P:</b> {n.plan}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
